import { formatNumber } from '@angular/common';
import { LOCALE_ID, Pipe, PipeTransform, inject } from '@angular/core';

import { CatalogSku } from '../common/CatalogSku';

/**
 * The small tax note shown next to a catalog price. Shared by the search result
 * card and the item detail page so the rules live in one place.
 *
 * It used to be one of two fixed sentences, and the common one claimed
 * "inkl. 19% MwSt." for every listing. Nothing verified that number — `CatalogSku`
 * carried no rate — so for every reduced-rate good (7% on books and food) the shop
 * printed a wrong tax statement under the price. The backend sends the listing's own
 * `vatPercentage` now, and the note is chosen from it.
 *
 * Three cases, in this order:
 *
 * 1. §19 UStG — a small-business seller charges no VAT at all, so no rate may appear.
 *    Tested first, and never reaches the rate whatever the listing carries.
 * 2. A known rate — named.
 * 3. No rate — an older backend, a listing with no VAT assigned, or the undetermined
 *    sentinel (backend #579), which the BFF maps to null rather than to its placeholder
 *    0%. The note then only states that VAT is included, which is what § 1 PAngV asks
 *    for; it does not invent a rate.
 *
 * The rate is formatted with `LOCALE_ID`, the locale of the bundle on screen, so it
 * carries the right decimal separator ("7,5" vs "7.5"). `1.0-2` drops the fraction of a
 * whole rate and keeps at most two digits, which is the precision the backend column
 * holds. The space before the percent sign is German typography and not English, so it
 * belongs to the translated message rather than to this code.
 */
@Pipe({
  name: 'priceNote',
})
export class PriceNotePipe implements PipeTransform {
  private readonly localeId = inject(LOCALE_ID);

  transform(catalogSku: CatalogSku): string {
    if (catalogSku?.smallBusinessP19UStG) {
      return $localize`:@@catalogItemCard.priceNote.smallBusiness:ohne USt. (§19 UStG)`;
    }

    const percentage = catalogSku?.vatPercentage;
    if (percentage === null || percentage === undefined) {
      return $localize`:@@catalogItemCard.priceNote.vat:inkl. MwSt.`;
    }

    const rate = formatNumber(percentage, this.localeId, '1.0-2');
    return $localize`:@@catalogItemCard.priceNote.vatRate:inkl. ${rate}:rate: % MwSt.`;
  }
}
